import React, { useState, useMemo } from 'react';
import axios from 'axios';
import { getAuth } from 'firebase/auth';
import { toast } from 'sonner';
import { RefreshCw, X, Layers, DollarSign } from 'lucide-react';
import { useCompany } from '../../../../context/CompanyContext';
import SeasonalSuggestion from './SeasonalSuggestion';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * EnrollmentSheet — Phase 2 create/edit form for a single enrollment.
 *
 * mode="add"  — POST /api/leads/{leadId}/enrollments
 *               Creates a NEW independent enrollment. Backend seeds Visit #1.
 * mode="edit" — PATCH /api/leads/{leadId}/enrollments/{enrollmentId}
 *               Updates editable fields on an active enrollment.
 *
 * Never writes to `lead.recurrence` directly. All writes flow through
 * the backend API so the enrollment doc stays authoritative and the
 * legacy mirror is updated by the service layer.
 */
const EnrollmentSheet = ({
  mode = 'add',       // 'add' | 'edit'
  enrollment = null,   // required for edit
  client,
  programs,
  onClose,
  onSaved,
}) => {
  const { companyId } = useCompany();
  const isEdit = mode === 'edit';
  const src = isEdit ? (enrollment || {}) : {};

  const [saving, setSaving] = useState(false);
  const [programName, setProgramName] = useState(src.programName || '');
  const [planLabel, setPlanLabel]     = useState(src.planLabel || '');
  const [servicePlanTemplateId, setServicePlanTemplateId] = useState(
    src.servicePlanTemplateId || '');
  const [interval, setInterval]       = useState(src.interval ?? 14);
  const [unit, setUnit]               = useState(src.unit || 'day');
  const [rollMode, setRollMode]       = useState(src.rollMode || 'schedule');
  const [limited, setLimited]         = useState(src.totalOccurrences != null);
  const [totalOccurrences, setTotal]  = useState(src.totalOccurrences ?? 12);
  const [startDate, setStartDate]     = useState(
    src.startDate ? src.startDate.substring(0, 10) : '');
  const [pricingAmount, setPricingAmount] = useState(
    src.pricingAmount != null ? String(src.pricingAmount) : '');

  const selectedProgram = useMemo(
    () => programs?.find((p) => p.id === servicePlanTemplateId) || null,
    [programs, servicePlanTemplateId]);

  const handleSave = async () => {
    if (!client?.id || !companyId) return;
    const iv = parseInt(interval, 10);
    if (!Number.isFinite(iv) || iv < 1) {
      toast.error('Interval must be at least 1');
      return;
    }
    let total = null;
    if (limited) {
      total = parseInt(totalOccurrences, 10);
      if (!Number.isFinite(total) || total < 1) {
        toast.error('Total occurrences must be at least 1');
        return;
      }
    }
    let price = null;
    if (pricingAmount !== '' && pricingAmount != null) {
      const n = Number(pricingAmount);
      if (!Number.isFinite(n) || n < 0) {
        toast.error('Price must be ≥ 0');
        return;
      }
      price = Number(n.toFixed(2));
    }

    setSaving(true);
    try {
      const token = await getAuth().currentUser?.getIdToken();
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const body = {
        servicePlanTemplateId: servicePlanTemplateId || null,
        programName: programName || (selectedProgram?.name ?? null),
        planLabel: planLabel || null,
        interval: iv,
        unit,
        rollMode,
        totalOccurrences: total,
        startDate: startDate || null,
        pricingAmount: price,
      };

      if (isEdit) {
        await axios.patch(
          `${API}/api/leads/${client.id}/enrollments/${enrollment.enrollmentId}`,
          body, { headers });
        toast.success('Program updated');
      } else {
        await axios.post(
          `${API}/api/leads/${client.id}/enrollments`,
          body, { headers });
        toast.success('Program added');
      }
      onSaved?.();
      onClose?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || (isEdit ? 'Failed to update program' : 'Failed to add program'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="bg-white rounded-md border border-gray-200 p-3"
      data-testid={isEdit ? 'enrollment-sheet-edit' : 'enrollment-sheet-add'}
    >
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <RefreshCw className="h-4 w-4 text-gray-400" />
          <h3 className="text-sm font-semibold text-gray-900">
            {isEdit ? 'Edit Program' : 'Add Program'}
          </h3>
        </div>
        <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
          <X className="h-4 w-4" />
        </button>
      </div>

      <div className="space-y-4">
        <div>
          <label className="text-xs font-medium text-gray-500 mb-1 flex items-center gap-1">
            <Layers className="h-3 w-3" /> Service Program
          </label>
          <select
            value={servicePlanTemplateId}
            onChange={(e) => {
              const pid = e.target.value;
              setServicePlanTemplateId(pid);
              const p = programs?.find((x) => x.id === pid);
              if (p && !programName) setProgramName(p.name || '');
              if (p?.suggestedIntervalDays && (parseInt(interval, 10) === 14 || !interval)) {
                setInterval(p.suggestedIntervalDays);
              }
            }}
            className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 bg-white focus:outline-none focus:border-emerald-400"
            data-testid="enrollment-program-select"
          >
            <option value="">— None (custom label) —</option>
            {programs?.map((p) => (
              <option key={p.id} value={p.id}>{p.name}</option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">
            Program Label {selectedProgram ? '(optional override)' : ''}
          </label>
          <input
            type="text"
            value={programName}
            onChange={(e) => setProgramName(e.target.value)}
            placeholder={selectedProgram?.name || 'e.g. Weekly Lawn Program'}
            className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:border-emerald-400"
            data-testid="enrollment-name-input"
          />
        </div>

        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">Operator Note (optional)</label>
          <input
            type="text"
            value={planLabel}
            onChange={(e) => setPlanLabel(e.target.value)}
            placeholder="e.g. Quarterly w/ termite check"
            className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:border-emerald-400"
            data-testid="enrollment-notes-input"
          />
        </div>

        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">Cadence</label>
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-500">Every</span>
            <input
              type="number" min="1" max="365"
              value={interval}
              onChange={(e) => setInterval(e.target.value)}
              className="w-16 text-sm border border-gray-200 rounded-lg px-2 py-2 text-center focus:outline-none focus:border-emerald-400"
              data-testid="enrollment-interval-input"
            />
            <select
              value={unit}
              onChange={(e) => setUnit(e.target.value)}
              className="text-sm border border-gray-200 rounded-lg px-2 py-2 bg-white focus:outline-none focus:border-emerald-400"
              data-testid="enrollment-unit-select"
            >
              <option value="day">day{parseInt(interval, 10) === 1 ? '' : 's'}</option>
              <option value="week">week{parseInt(interval, 10) === 1 ? '' : 's'}</option>
              <option value="month">month{parseInt(interval, 10) === 1 ? '' : 's'}</option>
            </select>
          </div>
        </div>

        <div>
          <label className="block text-xs font-medium text-gray-500 mb-2">Occurrences</label>
          <div className="space-y-2">
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="radio" checked={!limited} onChange={() => setLimited(false)}
                     className="text-emerald-600" data-testid="enrollment-unlimited-radio" />
              <span className="text-sm text-gray-700">Unlimited</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="radio" checked={limited} onChange={() => setLimited(true)}
                     className="text-emerald-600" data-testid="enrollment-limited-radio" />
              <span className="text-sm text-gray-700">Limited —</span>
              {limited && (
                <input type="number" min="1" value={totalOccurrences}
                       onChange={(e) => setTotal(e.target.value)}
                       className="w-14 text-sm border border-gray-200 rounded px-2 py-1 text-center focus:outline-none focus:border-emerald-400"
                       data-testid="enrollment-total-input" />
              )}
              {limited && <span className="text-sm text-gray-700">visits</span>}
            </label>
            {/* Sprint 310 — restored seasonal suggestion (parity with legacy ProgramSheet). */}
            <SeasonalSuggestion
              programs={programs}
              selectedProgramId={servicePlanTemplateId}
              startDate={startDate}
              intervalDays={interval}
              limited={limited}
              currentCount={totalOccurrences}
              onUseSuggestion={(n) => { setLimited(true); setTotal(n); }}
            />
          </div>
        </div>

        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">Start Date (optional)</label>
          <input type="date" value={startDate}
                 onChange={(e) => setStartDate(e.target.value)}
                 className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:border-emerald-400"
                 data-testid="enrollment-start-date-input" />
        </div>

        <div>
          <label className="text-xs font-medium text-gray-500 mb-1 flex items-center gap-1">
            <DollarSign className="h-3 w-3" /> Price per visit (optional)
          </label>
          <div className="relative">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">$</span>
            <input type="number" min="0" step="0.01" value={pricingAmount}
                   onChange={(e) => setPricingAmount(e.target.value)}
                   placeholder="0.00"
                   className="w-full text-sm border border-gray-200 rounded-lg pl-7 pr-3 py-2 focus:outline-none focus:border-emerald-400"
                   data-testid="enrollment-price-input" />
          </div>
        </div>

        <div>
          <label className="block text-xs font-medium text-gray-500 mb-2">Rolling Behavior</label>
          <div className="space-y-2">
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="radio" checked={rollMode === 'schedule'} onChange={() => setRollMode('schedule')}
                     className="text-emerald-600" data-testid="enrollment-rollmode-schedule" />
              <span className="text-sm text-gray-700">Schedule-based</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="radio" checked={rollMode === 'completion'} onChange={() => setRollMode('completion')}
                     className="text-emerald-600" data-testid="enrollment-rollmode-completion" />
              <span className="text-sm text-gray-700">Completion-based</span>
            </label>
          </div>
        </div>

        <div className="flex gap-2 pt-1">
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex-1 text-sm font-medium py-2 rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50 transition-colors"
            data-testid="enrollment-save-btn"
          >
            {saving
              ? (isEdit ? 'Saving…' : 'Adding…')
              : (isEdit ? 'Save changes' : 'Add Program')}
          </button>
          <button
            onClick={onClose}
            className="px-4 text-sm border border-gray-200 rounded-lg text-gray-600 hover:bg-gray-50 transition-colors"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

export default EnrollmentSheet;
